import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import StepIndicator from "@/components/booking/StepIndicator";
import WizardStep from "@/components/booking/WizardStep";
import SuccessScreen from "@/components/booking/SuccessScreen";
import { validateDetails, detailsAreValid } from "@/components/booking/validation";
import { createBooking } from "@/lib/api";

const initialDetails = {
  owner_first_name: "",
  owner_last_name: "",
  owner_phone: "",
  owner_email: "",
  pet_name: "",
  pet_breed: "",
  pet_notes: "",
};

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookingWizard({ services, availability, settings }) {
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(new Set());
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [details, setDetails] = useState(initialDetails);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  const detailsValid = useMemo(() => detailsAreValid(details), [details]);

  const canProceed = (() => {
    if (step === 1) return selectedServiceIds.length > 0;
    if (step === 2) return Boolean(selectedDate && selectedTime);
    if (step === 3) return detailsValid;
    return true;
  })();

  const markCompleted = (id) =>
    setCompleted((prev) => new Set(prev).add(id));

  const goNext = () => {
    if (step === 3) {
      const newErrors = validateDetails(details);
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) return;
    }
    markCompleted(step);
    setStep((s) => Math.min(4, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const jumpTo = (id) => {
    if (id < step || completed.has(id)) setStep(id);
  };

  const submitBooking = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const booking = await createBooking({
        owner_first_name: details.owner_first_name,
        owner_last_name: details.owner_last_name,
        owner_phone: details.owner_phone,
        owner_email: details.owner_email,
        pet_name: details.pet_name,
        pet_breed: details.pet_breed || "",
        pet_notes: details.pet_notes || "",
        services: selectedServiceIds,
        appointment_date: fmtDate(selectedDate),
        appointment_time: selectedTime,
      });
      markCompleted(4);
      setConfirmedBooking(booking);
      toast.success("Booking confirmed!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setStep(1);
    setCompleted(new Set());
    setSelectedServiceIds([]);
    setSelectedDate(null);
    setSelectedTime(null);
    setDetails(initialDetails);
    setErrors({});
    setConfirmedBooking(null);
  };

  if (confirmedBooking) {
    return (
      <SuccessScreen
        booking={confirmedBooking}
        onBookAnother={resetForAnother}
        businessName={settings.business_name}
      />
    );
  }

  return (
    <div className="pp-card flex flex-col" data-testid="booking-wizard">
      <div className="px-5 md:px-8 pt-5 md:pt-6">
        <StepIndicator currentStep={step} completedSteps={completed} onJumpTo={jumpTo} />
      </div>

      <div className="px-5 md:px-8 pb-4">
        <WizardStep
          step={step}
          services={services}
          availability={availability}
          selectedServiceIds={selectedServiceIds}
          setSelectedServiceIds={setSelectedServiceIds}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          details={details}
          setDetails={setDetails}
          errors={errors}
          setErrors={setErrors}
          onJumpTo={(id) => setStep(id)}
        />
      </div>

      <div className="pp-action-bar px-5 md:px-8 py-4 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {step > 1 ? (
          <button type="button" onClick={goBack} className="pp-btn-ghost" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        ) : (
          <span className="hidden sm:block" />
        )}

        {step < 4 ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canProceed}
            className="pp-btn-primary w-full sm:w-auto"
            data-testid="next-btn"
          >
            Next step
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submitBooking}
            disabled={submitting}
            className="pp-btn-confirm w-full sm:w-auto"
            data-testid="confirm-booking-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Confirming…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" /> Confirm booking
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
